import { AppData, Lehrperson, Klasse, LehrpersonStats } from "../types";
import { AlertCircle, TrendingUp, CheckCircle } from "lucide-react";
import { calculateLehrpersonStats } from "./calculations";
import { STATUS_THRESHOLDS } from "./constants";

export interface StatusConfig {
  bg: string;
  text: string;
  icon: typeof AlertCircle | typeof TrendingUp | typeof CheckCircle;
}

/**
 * Get the status configuration (colors and icon) based on status
 * @param status - The status of the Lehrperson
 * @returns Configuration object with background color, text color, and icon
 */
export const getStatusConfig = (
  status: LehrpersonStats["status"]
): StatusConfig => {
  if (status === "Unterbelastet") {
    return {
      bg: "bg-amber-100",
      text: "text-amber-800",
      icon: AlertCircle,
    };
  }
  if (status === "Überbelastet") {
    return {
      bg: "bg-red-100",
      text: "text-red-800",
      icon: TrendingUp,
    };
  }
  return {
    bg: "bg-green-100",
    text: "text-green-800",
    icon: CheckCircle,
  };
};

/**
 * Convert an array of objects to select options format
 * @param items - Array of objects with id property
 * @param labelKey - The key to use for the label
 * @returns Array of {id, label} objects
 */
export const toSelectOptions = <T extends { id: string; [key: string]: any }>(
  items: T[],
  labelKey: keyof T
): Array<{ id: string; label: string }> => {
  return items.map((item) => ({
    id: item.id,
    label: String(item[labelKey]),
  }));
};

/**
 * Get all Zuteilungen for a specific Lehrperson
 * @param lpId - The Lehrperson ID
 * @param data - The application data
 * @returns Array of Zuteilungen for the Lehrperson
 */
export const getZuteilungenByLehrperson = (lpId: string, data: AppData) => {
  return data.zuteilungen.filter((zt) => zt.lpId === lpId);
};

/**
 * Get all Zuteilungen for a specific Klasse
 * @param klasseId - The Klasse ID
 * @param data - The application data
 * @returns Array of Zuteilungen for the Klasse
 */
export const getZuteilungenByKlasse = (klasseId: string, data: AppData) => {
  return data.zuteilungen.filter((zt) => zt.klasseId === klasseId);
};

/**
 * Get a Klasse by ID
 * @param id - The Klasse ID
 * @param klassen - Array of Klassen
 * @returns The Klasse or undefined
 */
export const getKlasseById = (id: string, klassen: Klasse[]) => {
  return klassen.find((k) => k.id === id);
};

/**
 * Get a Lehrperson by ID
 * @param id - The Lehrperson ID
 * @param lehrpersonen - Array of Lehrpersonen
 * @returns The Lehrperson or undefined
 */
export const getLehrpersonById = (id: string, lehrpersonen: Lehrperson[]) => {
  return lehrpersonen.find((lp) => lp.id === id);
};

/**
 * Calculate stats for all Lehrpersonen
 * @param data - The application data
 * @returns Array of Lehrpersonen with calculated stats
 */
export const calculateAllLehrpersonenStats = (data: AppData) => {
  return data.lehrpersonen.map((lp) => ({
    ...lp,
    stats: calculateLehrpersonStats(lp.id, data),
  }));
};

/**
 * Calculate summary statistics for the application
 * @param data - The application data
 * @returns Summary object with counts and statistics
 */
export const calculateDataSummary = (data: AppData) => {
  const totalLehrpersonen = data.lehrpersonen.length;
  const totalKlassen = data.klassen.length;
  const totalZuteilungen = data.zuteilungen.length;
  const totalSchueler = data.klassen.reduce((sum, kl) => sum + kl.bestand, 0);

  const lehrpersonenStats = calculateAllLehrpersonenStats(data);

  const unterbelastet = lehrpersonenStats.filter(
    (lp) => lp.stats.differenz < STATUS_THRESHOLDS.UNDERLOAD
  ).length;
  const ausgeglichen = lehrpersonenStats.filter(
    (lp) =>
      Math.abs(lp.stats.differenz) <= Math.abs(STATUS_THRESHOLDS.OVERLOAD)
  ).length;
  const ueberbelastet = lehrpersonenStats.filter(
    (lp) => lp.stats.differenz > STATUS_THRESHOLDS.OVERLOAD
  ).length;

  return {
    totalLehrpersonen,
    totalKlassen,
    totalZuteilungen,
    totalSchueler,
    unterbelastet,
    ausgeglichen,
    ueberbelastet,
    lehrpersonenStats,
  };
};
